import * as angular from 'angular';
import { Observable, combineLatest, of } from 'rxjs';
import { filter, map, tap } from 'rxjs/operators';

import { CellActions } from '~/actions/notebooks/cell.actions';
import { AppStore } from '~/types/main.types';
import { NotebookTypes } from '@mode/notebooks/contract';

import { SessionResource } from '@mode/legacy/data-access-model-js';
import {
  NormalizedNotebookCellExRes,
  NormalizedNotebookCellRunResult,
  RouterStateHelpers,
} from '@mode/legacy/types-app-state';
import { NotebookSelectors } from '../../../selectors/notebook.selectors';
import { NotebookDataService } from '../../../services/notebook-data-service';
import { DataViewTypes, FeatureFlag, FeatureFlagsFacade } from '@mode/shared/contract-common';
import { NotebookStateService } from '../notebook/notebook-state-service';
import { CellExportFacade } from '@mode/notebooks/contract';
import { StateService } from '@uirouter/core';

class NotebookCellGroupController implements angular.IController {
  static $inject = [
    'RxStoreService',
    'NotebookDataService',
    'FeatureFlagsFacade',
    'NotebookStateService',
    'CellExportFacade',
    '$state',
  ];

  cellToken: string;
  cellContentType: NotebookTypes.ContentType;
  notebookToken: string;
  session?: SessionResource;
  readOnly: boolean;

  private results$: Observable<NormalizedNotebookCellRunResult[]>;
  private executionCount$: Observable<number>;
  private showResult$: Observable<boolean>;
  private hasResult$: Observable<boolean>;

  private cell$: Observable<NormalizedNotebookCellExRes>;
  private cellExecuting$: Observable<boolean>;
  private cellToken$: Observable<string>;
  private contentType$: Observable<NotebookTypes.ContentType>;
  private nextCellToken$: Observable<string>;
  private prevCellToken$: Observable<string>;
  private isInLayout$: Observable<boolean>;
  private isEditing$: Observable<boolean>;
  private isSelected$: Observable<boolean>;
  private isFrozen$: Observable<boolean>;
  private isFolded$: Observable<boolean>;
  private canFreeze$: Observable<boolean>;
  private canRunCell$: Observable<boolean>;
  private canDelete$: Observable<boolean>;

  private isExportDatasetsEnabled$: Observable<boolean>;
  private isNotebookMigrationEnabled$: Observable<boolean> = of(false);
  private isCellExported$: Observable<boolean>;
  private exportedDatasetResult$: Observable<DataViewTypes.DatasetResult | null>;

  private readonly EXPORT_TOOLTIP_HTML_CONTENT = `<div class="indicator-tooltip-go-to-dataset">
  <div class='indicator-tooltip'>Output is in use as a local dataset in this Report</div>
  <button class="go-to-dataset-button">Go to Dataset</button>
</div>`;
  private tooltipButtonSelectors = ['.go-to-dataset-button'];

  constructor(
    private rxStore: AppStore,
    private notebookDataService: NotebookDataService,
    private featureFlagsFacade: FeatureFlagsFacade,
    private notebookStateService: NotebookStateService,
    private cellExportFacade: CellExportFacade,
    private $state: StateService
  ) {}

  $onInit() {
    this.cell$ = this.rxStore.select(NotebookSelectors.createNormalizedCellExResSelector(this.cellToken)).pipe(
      filter((c) => c !== null),
      tap((cell) => (this.cellContentType = cell.content_type))
    );
    this.hasResult$ = this.rxStore.select(NotebookSelectors.selectedCellHasResult);
    this.executionCount$ = this.rxStore.select(NotebookSelectors.createCellExecutionCountSelector(this.cellToken));
    this.results$ = this.rxStore
      .select(NotebookSelectors.createLastResultsByCellSelector(this.cellToken))
      .pipe(filter((r) => r !== null));
    this.isInLayout$ = this.rxStore.select(NotebookSelectors.createIsCellInLayoutSelector(this.cellToken));
    this.isEditing$ = this.rxStore.select(NotebookSelectors.createIsCellEditingSelector(this.cellToken));
    this.isSelected$ = combineLatest([this.rxStore.select(NotebookSelectors.selectedCellToken), this.cell$]).pipe(
      map(([selectedCellToken, cell]) => !this.readOnly && cell.token === selectedCellToken)
    );
    this.showResult$ = combineLatest([this.results$, this.cell$]).pipe(
      map(([results, c]) => c.content_type === NotebookTypes.ContentType.Python && results.length > 0 && !c.folded)
    );

    this.cellExecuting$ = this.rxStore.select(NotebookSelectors.selectedCellExecuting);
    this.canRunCell$ = this.rxStore.select(NotebookSelectors.createCanCellRunSelector(this.notebookToken));
    this.cellToken$ = this.rxStore.select(NotebookSelectors.selectedCellToken);
    this.nextCellToken$ = this.rxStore.select(NotebookSelectors.selectedNextCellToken);
    this.prevCellToken$ = this.rxStore.select(NotebookSelectors.selectedPrevCellToken);
    this.isFrozen$ = this.rxStore.select(NotebookSelectors.selectedCellFrozen);
    this.isFolded$ = this.rxStore.select(NotebookSelectors.selectedCellFolded);
    this.canFreeze$ = this.rxStore.selectWithParam(NotebookSelectors.canFreezeByCell, this.cellToken$);
    this.contentType$ = this.rxStore.select(NotebookSelectors.selectedCellContentType);

    this.isExportDatasetsEnabled$ = this.featureFlagsFacade.asObservable(FeatureFlag.NotebookExportDatasets);
    this.isNotebookMigrationEnabled$ = this.featureFlagsFacade.asObservable(FeatureFlag.NotebookMigrationPhase1);
    this.isCellExported$ = this.rxStore.selectWithParam(NotebookSelectors.isExportedByCell, of(this.cellToken));

    this.canDelete$ = this.notebookStateService.canDelete$;
    this.exportedDatasetResult$ = this.cellExportFacade.getDatasetResultByCellToken(this.cellToken);
  }

  selectCell() {
    this.notebookDataService.selectCell(this.cellToken, true);
  }

  runCell(params: { cellToken: string; contentType: NotebookTypes.ContentType; nextCellToken: string }) {
    const { nextCellToken } = params;

    this.notebookDataService.runCell(this.notebookToken, this.cellToken);

    if (!nextCellToken) {
      this.notebookDataService.addCell(this.notebookToken);
    } else {
      this.notebookDataService.selectCell(nextCellToken, false);
    }
  }

  moveCell(direction: 'up' | 'down') {
    this.notebookDataService.moveCell(this.cellToken, direction === 'down');
  }

  deleteCell() {
    this.notebookDataService.removeCell(this.notebookToken, this.cellToken);
  }

  insertCellAbove() {
    this.notebookDataService.addCell(this.notebookToken, this.cellToken);
  }

  insertCellBelow(nextCellToken: string) {
    this.notebookDataService.addCell(this.notebookToken, nextCellToken);
  }

  toggleFreezeCell() {
    this.rxStore.dispatch({
      type: CellActions.Type.ToggleFreezeCell,
      cellToken: this.cellToken,
    });
  }

  toggleFoldCell() {
    this.rxStore.dispatch({
      type: CellActions.Type.FoldCell,
      cellToken: this.cellToken,
    });
  }

  toggleIncludedInReportState(state: boolean) {
    if (state) {
      this.notebookDataService.removeCellFromLayout(this.cellToken);
    } else {
      this.notebookDataService.addCellToLayout(this.cellToken);
    }
  }

  updateContentType(cellType: NotebookTypes.ContentType) {
    this.notebookDataService.updateContentType(this.cellToken, cellType);
  }

  goToDataset(exportedDatasetResult: DataViewTypes.DatasetResult) {
    if (!exportedDatasetResult) return;
    else if (this.$state.includes('**.reports.edit') || this.$state.includes('**.reports.edit.**')) {
      this.$state.go('application.reports.edit.datasets', {
        switchviewToken: exportedDatasetResult.switchViewToken,
      });
    } else if (this.$state.includes('reportShow.noRuns')) {
      this.$state.go('reportShow.noRuns.detailsView.detailsDataset', {
        queryId: exportedDatasetResult.queryToken,
      });
    } else {
      this.$state.go('reportShow.runs.detailsView.detailsDataset', {
        queryId: exportedDatasetResult.queryToken,
      });
    }
  }
}

export const notebookCellGroupComponent: angular.IComponentOptions = {
  bindings: {
    cellToken: '<',
    notebookToken: '<',
    session: '<?',
    readOnly: '@',
  },
  controller: NotebookCellGroupController,
  controllerAs: 'ctrl',
  template: `
    <div id="{{ ::ctrl.cellToken }}" class="mode-notebook-cell-group clearfix --with-cell-toolbar" ng-class="{
      'editing': (ctrl.isEditing$ | async:this),
      'cell-selected': (ctrl.isSelected$ | async:this),
      '--in-layout-output-collapsed': !(ctrl.showResult$ | async:this) && (ctrl.isInLayout$ | async:this)
    }">
      <div class="cell-indicator"></div>
      <!-- Cell Toolbar -->
      <mode-notebook-cell-menu ng-if="(ctrl.isSelected$ | async:this)"
        [cell-token]="ctrl.cellToken"
        [content-type]="ctrl.cellContentType"
        [prev-cell-token]="ctrl.prevCellToken$ | async:this"
        [next-cell-token]="ctrl.nextCellToken$ | async:this"
        [has-result]="ctrl.hasResult$ | async:this"
        [included-in-report]="ctrl.isInLayout$ | async:this"
        [is-folded]="ctrl.isFolded$ | async:this"
        [is-frozen]="ctrl.isFrozen$ | async:this"
        [can-run]="ctrl.canRunCell$ | async:this"
        [can-freeze]="ctrl.canFreeze$ | async:this"
        [can-delete]="(ctrl.canDelete$ | async:this)"
        (run-cell)="ctrl.runCell($event)"
        (move-cell)="ctrl.moveCell($event)"
        (delete-cell)="ctrl.deleteCell($event)"
        (insert-cell-above)="ctrl.insertCellAbove($event)"
        (insert-cell-below)="ctrl.insertCellBelow($event)"
        (toggle-freeze-state)="ctrl.toggleFreezeCell($event)"
        (toggle-fold-state)="ctrl.toggleFoldCell($event)"
        (toggle-included-in-report-state)="ctrl.toggleIncludedInReportState($event)"
        (content-type-change)="ctrl.updateContentType($event)">
      </mode-notebook-cell-menu>

      <!-- Cell Input -->
      <div ng-if="!(ctrl.isNotebookMigrationEnabled$ |async:this)">
        <mode-notebook-cell-editor ng-if="ctrl.cell$ | async:this"
          read-only="ctrl.readOnly"
          cell="ctrl.cell$ | async:this"
          notebook-token="ctrl.notebookToken"
          session="ctrl.session"
          show-in-layout="!(ctrl.showResult$ | async:this) && (ctrl.isInLayout$ | async:this)">
        </mode-notebook-cell-editor>
      </div>
      <div ng-if="(ctrl.isNotebookMigrationEnabled$ |async:this)">
        <mode-notebook-editor-cell ng-if="ctrl.cell$ | async:this"
          [read-only]="ctrl.readOnly"
          [cell]="ctrl.cell$ | async:this"
          [notebook-token]="ctrl.notebookToken"
          [session]="ctrl.session"
          [show-in-layout]="!(ctrl.showResult$ | async:this) && (ctrl.isInLayout$ | async:this)">
        </mode-notebook-editor-cell>
      </div>

      <!-- Cell Output -->
      <div ng-if="ctrl.showResult$ | async:this" class="mode-notebook-cell cell-result">
        <div class="mode-notebook-cell-header">OUT [{{ (ctrl.executionCount$ | async:this) || '_' }}]</div>
        <mode-state-indicator-icon ng-if="!(ctrl.isExportDatasetsEnabled$ | async:this) && (ctrl.isInLayout$ | async:this)"
          display-state-icon="true"
          main-icon="icon-cell"
          tooltip-placement="bottom"
          state-icon="icon-check"
          tooltip-html-content="This cell is included in the report"
          class="notebook-cell-state-indicator --output-cell"
          data-test="notebook-cell-state-indicator"
        ></mode-state-indicator-icon>
        <div class="notebook-cell-state-indicators --output" ng-if="(ctrl.isExportDatasetsEnabled$ | async:this)">
          <mode-state-indicator-icon ng-if="(ctrl.isInLayout$ | async:this)"
            display-state-icon="true"
            main-icon="icon-cell"
            tooltip-placement="bottom"
            state-icon="icon-check"
            tooltip-html-content="Output has been added to Report Builder"
            class="--output-cell"
            data-test="notebook-cell-state-indicator"
          ></mode-state-indicator-icon>
          <mode-state-indicator-icon ng-if="(ctrl.isCellExported$ | async:this)"
            display-state-icon="true"
            main-icon="icon-dataset"
            tooltip-placement="bottom"
            state-icon="icon-check"
            class="--export-cell"
            data-test="notebook-cell-state-indicator"
            [tooltip-html-content]="ctrl.EXPORT_TOOLTIP_HTML_CONTENT"
            [tooltip-button-selectors]="ctrl.tooltipButtonSelectors"
            (tooltip-button-click)="ctrl.goToDataset(ctrl.exportedDatasetResult$ | async:this)"
          ></mode-state-indicator-icon>
        </div>
        <mode-notebook-cell-warning [cells]="ctrl.results$ | async:this"></mode-notebook-cell-warning>

        <mode-notebook-cell-result
          ng-repeat="result in (ctrl.results$ | async:this) | orderBy:'date' | filter:{result_type:'!stderr'}; track by result.token"
          [cell]="result" on-click="!(ctrl.isSelected$ | async:this) && ctrl.selectCell()"
        ></mode-notebook-cell-result>
        <div class="hoverbox"></div>
      </div>
    </div>
  `,
};
